/**
 * The 4×6 shipping-label print portal (P5 S3) — the FIFTH body-level print portal, alongside the
 * pick list, the PO, the press pull sheet and the garment label sheet. Same mechanism: a portal onto
 * `document.body` with its own id, `@media print` isolation in `globals.css` (the id MUST be in that
 * `:not()` chain or the portal hides itself), and a `@page` injected only between beforeprint and
 * afterprint so a 4×6 never bleeds into a letter print elsewhere in the app.
 *
 * The label is the carrier's own PNG, rendered by the provider at 300 dpi — we print the image, we do
 * not redraw it. That means NONE of the garment-label thermal constraints apply here: a carrier label
 * is born thermal-safe (pure 1-bit black on white, barcodes at the right module width). The single
 * rule is not to scale it: `width: 4in; height: 6in` exactly, or the barcode stops scanning.
 *
 * Reprinting is FREE and idempotent — it re-renders the label already bought. It never calls the
 * provider, so it can never be a second purchase.
 */
import { Printer, X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';

const LABEL_CSS = `
/* The portal exists only to be PRINTED. Left visible it adds a 4x6 white sheet below the fold of the
   kiosk and paints the label a second time — the other four portals avoid this by BEING the overlay;
   this one is a bare sheet, so it hides itself off-print. */
#ship-label-print { display: none; }
@media print { #ship-label-print { display: block; background: #fff; } }
#ship-label-print .sheet {
  width: 4in; height: 6in; margin: 0; padding: 0; overflow: hidden;
  display: flex; align-items: center; justify-content: center; background: #fff;
}
/* The carrier PNG is already 4×6 at 300dpi. contain (never cover/scale-up) so the barcode's module
   width survives — a stretched barcode is an unscannable one. */
#ship-label-print .sheet img { width: 4in; height: 6in; object-fit: contain; display: block; }
`;

/**
 * `label_format: 'PNG'` is a REQUEST, not a guarantee — some carrier/service combinations return a
 * PDF (or ZPL), which is why `labelFileType` is recorded on the shipment and threaded here. An
 * `<img>` pointed at a PDF renders broken on screen and prints a BLANK 4x6 to the Rollo — a silent
 * blank label on a box, the worst outcome available. So a non-image label is never printed from this
 * portal; it opens in the browser's own viewer instead.
 */
function isPrintableImage(labelFileType: string | undefined): boolean {
  // Undefined = a record from before the field was captured; those were all PNG.
  return labelFileType === undefined || labelFileType.startsWith('image/');
}

export function ShipLabelPrint({
  labelUrl,
  labelFileType,
  orderName,
  trackingCode,
  onClose,
}: {
  labelUrl: string;
  labelFileType?: string | undefined;
  orderName: string;
  trackingCode: string;
  onClose: () => void;
}) {
  const printable = isPrintableImage(labelFileType);
  // Inject the 4×6 page size ONLY while printing this portal (the global @page sets margins for
  // letter output and has no `size`, so this later rule wins on both while it is mounted).
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = '@page { size: 4in 6in; margin: 0; }';
    const before = () => {
      if (!el.isConnected) document.head.appendChild(el);
    };
    const after = () => el.remove();
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
      el.remove();
    };
  }, []);

  return (
    <>
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-black/80 p-6 print:hidden">
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="text-sm font-semibold">
              {orderName} · <span className="font-mono">{trackingCode}</span>
            </div>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={onClose}>
              <X className="size-4" />
            </Button>
          </div>
          {printable ? (
            /* Same image the portal prints, so what the operator approves is what the Rollo gets. */
            <img
              src={labelUrl}
              alt={`Shipping label for ${orderName}`}
              className="h-[60vh] w-auto bg-white object-contain"
            />
          ) : (
            <div className="max-w-sm rounded-lg border border-amber-800 bg-amber-950/40 p-4 text-sm text-amber-100">
              <b>This label is a {labelFileType}, not an image.</b>
              <p className="mt-1">
                Printing it from here would send a blank 4×6 to the Rollo. Open it and print from
                the viewer instead — it is the label already bought, so opening it costs nothing.
              </p>
            </div>
          )}
          <div className="mt-3 flex gap-2">
            {printable ? (
              <Button onClick={() => window.print()}>
                <Printer className="size-4" /> Print 4×6
              </Button>
            ) : (
              <Button asChild>
                <a href={labelUrl} target="_blank" rel="noreferrer">
                  <Printer className="size-4" /> Open the label
                </a>
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              Done
            </Button>
          </div>
          <p className="mt-2 max-w-sm text-xs text-muted-foreground">
            Prints to this station’s default printer — the bench Rollo. Reprinting costs nothing: it
            re-prints the label already bought, never a second purchase.
          </p>
        </div>
      </div>
      {printable
        ? createPortal(
            <div id="ship-label-print">
              <style>{LABEL_CSS}</style>
              <div className="sheet">
                <img src={labelUrl} alt="" />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
